import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Stack, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import HorizontalBar from './HorizontalBar';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import FooterSection from './Footer/FooterSection';
import SectionWrapper from './Footer/SectionWrapper';

// assets
import { IconChevronRight } from '@tabler/icons-react';

// styles - Box-based instead of styled to avoid theme frozen issues in MUI v7

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();

    const footerRef = useRef(null);
    const sidebarRef = useRef(null);
    const [sidebarHeight, setSidebarHeight] = useState('');
    const [footerTop, setFooterTop] = useState(0);

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { container, drawerType, layout } = useConfig();

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerType]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (matchDownMd) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    const header = useMemo(
        () => (
            <Toolbar sx={{ p: condition ? '10px' : '16px' }}>
                <Header />
            </Toolbar>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layout, matchDownMd]
    );

    useEffect(() => {
        const handleScroll = () => {
            const newFooterTop = footerRef?.current?.getBoundingClientRect().top;
            setSidebarHeight(`${Math.ceil(newFooterTop - 80)}px`); //trừ 80px cho header
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const footer = useMemo(
        () => (
            <SectionWrapper
                sx={{ bgcolor: theme.palette.mode === 'dark' ? 'background.default' : theme.palette.main.lightItems, pb: 0, zIndex: 1599 }}
            >
                <FooterSection />
            </SectionWrapper>
        ),
        [layout, matchDownMd]
    );

    return (
        <Container maxWidth={container ? 'lg' : false} {...(!container && { sx: { px: { xs: 0 } } })}>
            <Stack direction="column">
                <Box sx={{ display: 'flex' }}>
                    <CssBaseline />
                    {/* header */}
                    <AppBar
                        position="fixed"
                        elevation={0}
                        sx={{
                            bgcolor: 'background.default',
                            transition: drawerOpen ? theme.transitions.create('width') : 'none'
                        }}
                    >
                        {header}
                    </AppBar>

                    {/* horizontal menu-list bar */}
                    {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd && <HorizontalBar height2={sidebarHeight} />}

                    {/* drawer */}
                    {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || matchDownMd) && <Sidebar height2={sidebarHeight} />}

                    {/* main content */}
                    <Box
                        component="main"
                        sx={{
                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark?.[800] : theme.palette.grey?.[100],
                            width: '100%',
                            minHeight: 'calc(100vh - 88px)',
                            flexGrow: 1,
                            padding: '20px',
                            paddingTop: '10px',
                            marginRight: '20px',
                            borderRadius: theme.shape?.borderRadius || '12px',
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            transition: theme.transitions.create('margin', {
                                easing: drawerOpen ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.shorter + 200
                            }),
                            marginLeft: drawerOpen
                                ? layout === 'horizontal'
                                    ? '20px'
                                    : 0
                                : layout === LAYOUT_CONST.VERTICAL_LAYOUT
                                  ? -(drawerWidth - 72)
                                  : '20px',
                            marginTop: '80px',
                            [theme.breakpoints.up('md')]: {
                                width: `calc(100% - ${drawerWidth}px)`
                            },
                            [theme.breakpoints.down('md')]: {
                                marginLeft: '20px',
                                width: `calc(100% - ${drawerWidth}px)`,
                                padding: '16px',
                                paddingTop: '8px',
                                marginTop: '80px'
                            },
                            [theme.breakpoints.down('sm')]: {
                                marginLeft: '10px',
                                width: `calc(100% - ${drawerWidth}px)`,
                                padding: '16px',
                                paddingTop: '8px',
                                marginRight: '10px',
                                marginTop: '80px'
                            }
                        }}
                    >
                        <Container maxWidth={container ? 'lg' : false} {...(!container && { sx: { px: { xs: 0 } } })}>
                            {/* breadcrumb */}
                            <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                            <Outlet />
                        </Container>
                    </Box>
                    {/* <Customization /> */}
                    {/* <Chatbot /> */}
                </Box>
                <Box
                    ref={footerRef}
                    sx={{
                        zIndex: 1099,
                        marginLeft: drawerOpen ? `${drawerWidth}px` : `${72}px`,
                        transition: theme.transitions.create('margin', {
                            easing: drawerOpen ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                            duration: drawerOpen ? theme.transitions.duration.shorter + 200 : theme.transitions.duration.shorter + 200
                        }),
                        [theme.breakpoints.down('md')]: {
                            marginLeft: 0
                        }
                    }}
                >
                    {footer}
                </Box>
            </Stack>
        </Container>
    );
};

export default MainLayout;
